
import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { BigFormModal } from "@/components/ui/big-form-modal";
import { doc, getDoc } from "firebase/firestore";
import { db, auth } from "@/services/firebaseService";
import { usePhotoUploadForm } from "../hooks/usePhotoUploadForm";
import { PhotoUploadForm } from "./photo-upload/PhotoUploadForm";
import { FileUploadSection } from "./photo-shared/FileUploadSection";
import { getCampLocation } from "@/services/campLocationService";
import { useAuth } from "@/context/AuthContext";
import TermsOfUseModal from "@/components/welcome/components/TermsOfUseModal";

interface PhotoUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  onSuccess: () => void;
}

export const PhotoUploadDialog: React.FC<PhotoUploadDialogProps> = ({
  isOpen,
  onClose,
  locationId,
  onSuccess
}) => {
  const { currentUser, refreshAuthToken } = useAuth();
  const isAnonOrToken = !!currentUser?.isAnonymous;
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string>("");
  const [organizationName, setOrganizationName] = useState<string>("");
  const [currentYOffset, setCurrentYOffset] = useState(0);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attemptedOrgLoad, setAttemptedOrgLoad] = useState(false);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [agreePermission, setAgreePermission] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [outsidePermission, setOutsidePermission] = useState(false);
  const [outsidePermissionExplanation, setOutsidePermissionExplanation] = useState("");

  // Create a memoized loadOrgInfo function to prevent recreating it on each render
  const loadOrgInfo = useCallback(async () => {
    if (!locationId) {
      console.error("PhotoUploadDialog: Missing locationId");
      return false;
    }
    
    console.log("PhotoUploadDialog: Loading organization info for locationId:", locationId);
    
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        console.error("PhotoUploadDialog: Firebase user not authenticated");
        toast.error("You need to be logged in to upload photos");
        return false;
      }
      
      // Do not force token refresh here to avoid quota issues
      
      if (!currentUser) {
        console.error("PhotoUploadDialog: User not authenticated");
        toast.error("You need to be logged in to upload photos");
        return false;
      }
      
      console.log("PhotoUploadDialog: Fetching location data with ID:", locationId);
      const locationData = await getCampLocation(db, locationId);
      
      if (!locationData) {
        console.error("PhotoUploadDialog: Location data not found");
        toast.error("Location data not found");
        return false;
      }
      
      if (locationData.providedByOrg) {
        let orgId;
        
        if (typeof locationData.providedByOrg === 'string') {
          orgId = locationData.providedByOrg;
        } else if (typeof locationData.providedByOrg === 'object' && locationData.providedByOrg !== null) {
          orgId = (locationData.providedByOrg as any).id || '';
        }
        
        console.log("PhotoUploadDialog: Extracted organization ID:", orgId);
        
        if (!orgId) {
          console.warn("PhotoUploadDialog: Failed to extract organization ID from location data; continuing without it (admin user?)");
          // Allow proceeding without orgId for admin users
          setOrganizationId("");
          setOrganizationName("");
          return true;
        }
        
        setOrganizationId(orgId);
        
        try {
          console.log("PhotoUploadDialog: Fetching organization details from document ID:", orgId);
          const orgDoc = await getDoc(doc(db, "campOrganizations", orgId));
          if (orgDoc.exists()) {
            const orgData = orgDoc.data() as any;
            const orgName = orgData.name || orgId;
            console.log("PhotoUploadDialog: Found organization name:", orgName);
            setOrganizationName(orgName);
          } else {
            console.log("PhotoUploadDialog: Organization document not found, using ID as name");
            setOrganizationName(orgId);
          }
          
          return true;
        } catch (err) {
          console.error("PhotoUploadDialog: Error fetching org details:", err);
          setOrganizationName(orgId);
          return true;
        }
      } else {
        console.warn("PhotoUploadDialog: Missing providedByOrg in location data; proceeding (admin user?)");
        // Allow proceeding without orgId for admin user
        setOrganizationId("");
        setOrganizationName("");
        return true;
      }
    } catch (error) {
      console.error("PhotoUploadDialog: Error loading location/organization info:", error);
      // Don't block the modal; allow upload with fallback
      return true;
    }
  }, [locationId, currentUser, refreshAuthToken]);

  useEffect(() => {
    let isMounted = true;
    
    // Only run this effect when the dialog opens and we haven't attempted org load
    const shouldLoadOrgInfo = isOpen && 
                             !isLoading && 
                             !attemptedOrgLoad && 
                             locationId;
    
    if (!shouldLoadOrgInfo) return;
    
    const loadOrganizationInfo = async () => {
      if (isMounted) setIsLoading(true);
      
      try {
        const success = await loadOrgInfo();
        
        if (isMounted) {
          if (!success) {
            setError("Failed to load organization information. You may still proceed.");
          } else {
            setError(null);
          }
        }
      } finally {
        if (isMounted) setIsLoading(false);
        if (isMounted) setAttemptedOrgLoad(true);
      }
    };
    
    loadOrganizationInfo();
    
    return () => {
      isMounted = false;
    };
  }, [isOpen, locationId, attemptedOrgLoad, isLoading, loadOrgInfo]);

  // Check user token claims to gate access
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          if (!cancelled) setHasPermission(false);
          return;
        }
        // Use current token without forced refresh to avoid quota
        const tokenResult = await user.getIdTokenResult();
        const role = (tokenResult.claims as any)?.role as string | undefined;
        const claimOrgId = (tokenResult.claims as any)?.organizationId as string | undefined;
        const orgId = organizationId || null;
        const allowed = role === 'admin_user' || (!!claimOrgId && !!orgId && claimOrgId === orgId);
        if (!cancelled) setHasPermission(allowed);
      } catch (e) {
        console.error('PhotoUploadDialog: permission check failed', e);
        if (!cancelled) setHasPermission(false);
      }
    };
    if (isOpen) {
      run();
    }
    return () => { cancelled = true; };
  }, [isOpen, organizationId, refreshAuthToken]);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setFile(null);
    setPreview(null);
    setCurrentYOffset(0);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const {
    form,
    uploading,
    uploadProgress,
    handleSubmit,
    isAdmin,
    organizationLoaded
  } = usePhotoUploadForm(locationId, onSuccess, organizationName);

  useEffect(() => {
    // Update the form when organizationName changes
    if (organizationName && form) {
      form.setValue("photoReference", organizationName);
    }
  }, [organizationName, form]);

  const handleFileChange = (selectedFile: File | null) => {
    setError(null);
    
    if (selectedFile) {
      console.log("PhotoUploadDialog: File selected:", selectedFile.name);
      
      if (!selectedFile.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }
      
      if (selectedFile.size > 8 * 1024 * 1024) {
        toast.error("Image must be less than 8MB");
        return;
      }
      
      setFile(selectedFile);
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreview(event.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const copyFromCaption = () => {
    const caption = form.getValues("caption");
    if (caption) {
      form.setValue("altText", caption);
    }
  };

  const onSubmit = async (values: any) => {
    console.log("PhotoUploadDialog: Submit button clicked");
    setError(null);
    
    if (!file) {
      toast.error("Please select a photo to upload");
      return;
    }
    
    // Fallback: if organizationId is missing (e.g., admin user), use locationId for storage path
    const orgForUpload = organizationId || locationId;
    
    try {
      // Make sure Firebase auth is ready
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        console.error("PhotoUploadDialog: Firebase user not authenticated");
        toast.error("You need to be logged in to upload photos");
        setError("Authentication required. Please refresh the page and log in again.");
        return;
      }
      
      // Avoid forced token refresh here; rely on existing token
      const tokenResult = await firebaseUser.getIdTokenResult();
      console.log("PhotoUploadDialog: Token claims before upload:", tokenResult.claims);
      if (!tokenResult.claims.role) {
        console.warn("PhotoUploadDialog: Missing role claim in token; proceeding if storage rules permit");
      }
      
      console.log("PhotoUploadDialog: Submitting form with:", {
        values: { ...values, yOffset: currentYOffset },
        fileName: file.name,
        organizationId: orgForUpload,
        firebaseAuthStatus: "authenticated",
        authUID: firebaseUser.uid
      });
      
      // Ensure yOffset is synced with the form
      if (currentYOffset !== values.yOffset) {
        values.yOffset = currentYOffset;
      }
      
      // Call the upload handler from usePhotoUploadForm
      await handleSubmit(values, file, orgForUpload, {
        outsidePermission,
        permissionExplanation: outsidePermissionExplanation
      });
      
      // Notify parent listeners to refresh photos
      window.dispatchEvent(new CustomEvent("photosUpdated", { detail: { locationId } }));
      
    } catch (error) {
      console.error("PhotoUploadDialog: Error during submission:", error);
      setError(error instanceof Error ? error.message : String(error));
    }
  };

  const handleYOffsetChange = (value: number) => {
    console.log("PhotoUploadDialog: Y-offset changed to:", value);
    setCurrentYOffset(value);
    form.setValue("yOffset", value);
  };

  return (
    <>
    <BigFormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Upload photo"
      saveLabel="Upload photo"
      isSaving={uploading}
      onSave={form.handleSubmit(onSubmit)}
      isDirty={!!file}
      showSaveButton={true}
      saveDisabled={(() => {
        const isCanva = ((form.watch("photoReference") || "").trim() === "Canva");
        return isAnonOrToken || (!isCanva && !(agreePermission || (isAdmin && outsidePermission)));
      })()}
      footerTop={!isAnonOrToken ? (
        <div className="w-full flex justify-end">
          <div className="max-w-[550px] flex flex-col gap-2">
            {isAdmin && !((form.watch("photoReference") || "").trim() === "Canva") && (
              <div className="w-full flex items-center gap-2 text-sm">
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={outsidePermission}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setOutsidePermission(checked);
                      if (checked) {
                        setAgreePermission(false);
                      }
                    }}
                  />
                  <span className="whitespace-nowrap">We received permission outside the system:</span>
                </label>
                <input
                  type="text"
                  className="flex-1 h-9 px-2 py-1 text-sm border rounded-md disabled:bg-gray-100"
                  placeholder="Brief explanation"
                  value={outsidePermissionExplanation}
                  onChange={(e) => setOutsidePermissionExplanation(e.target.value)}
                  disabled={!outsidePermission}
                />
              </div>
            )}
            {((form.watch("photoReference") || "").trim() !== "Canva") && (
              <label className="w-full flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={agreePermission}
                  onChange={(e) => setAgreePermission(e.target.checked)}
                  disabled={isAdmin && outsidePermission}
                />
                <span className="flex-1">
                  I am able to grant permission to use this image, and I give KidsBooked permission to use this image in accordance with the <button type="button" onClick={() => setShowTerms(true)} className="text-kidsbooked-blue hover:underline">Terms of Use</button>.<span className="text-red-500">*</span>
                </span>
              </label>
            )}
          </div>
        </div>
      ) : undefined}
    >
      <div className="space-y-6">
        {hasPermission === false ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-600">
              You do not have permission to upload photos for this organization. Contact support@kidsbooked.com for assistance.
            </p>
          </div>
        ) : (
          <>
            {isAnonOrToken && (
              <div className="p-4 border rounded-md bg-yellow-50 border-yellow-200">
                <h3 className="font-medium text-yellow-900 mb-1">We need your permission first</h3>
                <p className="text-sm text-yellow-900 mb-3">Please create an account so we can be sure that we have your permission to use photos of your camp.</p>
                <a
                  href="/create-account"
                  className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                >
                  Create account
                </a>
              </div>
            )}
            <FileUploadSection
              preview={preview}
              fileName={file?.name || null}
              uploading={uploading}
              uploadProgress={uploadProgress}
              onFileChange={handleFileChange}
              onRemove={resetForm}
              disabled={uploading || !locationId || isAnonOrToken}
            />

            {preview && (
              <PhotoUploadForm
                form={form}
                isAdmin={isAdmin}
                disabled={uploading || isAnonOrToken}
                onCopyFromCaption={copyFromCaption}
                onYOffsetChange={handleYOffsetChange}
                previewImageSrc={preview}
              />
            )}
            
            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
          </>
        )}
      </div>
    </BigFormModal>
    <TermsOfUseModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
    </>
  );
};
